import React, { useEffect, useState } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import MaterialDataGrid from "../../components/MaterialDataGrid";
import MaterialTypography from "../../components/MaterialTypography";
import { Card, Stack, CardHeader, Chip } from "@mui/material";
import MaterialButton from "../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BASEURL from "../Utils/Baseurl";
import { useUserDetails } from "../../context/auth";
import { useLoading } from "../../context/loading";

const formatTime12hr = (timeStr) => {
  if (!timeStr) return "-";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hr12 = hour % 12 || 12;
  return `${hr12}:${m} ${ampm}`;
};

const Shift = () => {
  const navigate = useNavigate();
  const { AccessData } = useUserDetails();
  const { circleLoading, setCirleLoading } = useLoading();
  const [pageState, setPageState] = useState({
    loading: false,
    error: false,
    message: "",
    state: "init",
  });

  const [shiftList, setShiftList] = useState([]);

  useEffect(() => {
    onShiftDetails();
  }, []);

  const shiftListTable = {
    columns: [
      {
        field: "name",
        headerName: "Shift Name",
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
      },
      {
        field: "timing",
        headerName: "Timing",
        flex: 1.2,
        minWidth: 180,
        disableColumnMenu: true,
        editable: false,
        sortable: false,
        renderCell: (params) => {
          const { startTime, endTime } = params.row;
          if (startTime && endTime) {
            return `${formatTime12hr(startTime)} - ${formatTime12hr(endTime)}`;
          }
          return "Duration Only";
        },
      },
      {
        field: "total",
        headerName: "Duration",
        flex: 0.6,
        minWidth: 90,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
        renderCell: (params) => `${params.value} hrs`,
      },
      {
        field: "shiftType",
        headerName: "Type",
        flex: 0.6,
        minWidth: 100,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
        renderCell: (params) => {
          const type = params.value;
          const colorMap = {
            DAY: "primary",
            NIGHT: "secondary",
            GENERAL: "default",
          };
          return (
            <Chip
              label={type || "General"}
              color={colorMap[type] || "default"}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "isOvernight",
        headerName: "Overnight",
        flex: 0.5,
        minWidth: 100,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
        renderCell: (params) =>
          params.value ? (
            <Chip label="Yes" color="warning" size="small" />
          ) : (
            <Chip label="No" size="small" variant="outlined" />
          ),
      },
      {
        field: "gracePeriod",
        headerName: "Grace (min)",
        flex: 0.5,
        minWidth: 90,
        disableColumnMenu: true,
        editable: false,
        sortable: true,
      },
    ],
    rows: shiftList.map((shift) => ({
      id: shift.id,
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime,
      total: (shift?.totalWorkingHours / 3600).toFixed(1),
      shiftType: shift.shiftType,
      isOvernight: shift.isOvernight,
      gracePeriod: shift.gracePeriodMinutes,
    })),
  };

  const handleNewShift = () => {
    navigate("/shift/add-shift");
  };

  const onShiftListClick = (e) => {
    if (e?.id) {
      navigate(`/shift/shift-details?id=${e?.id}`);
    }
  };

  const onShiftDetails = async () => {
    setCirleLoading(true);
    const query = `
        query allShifts{
  allShifts {
    id
    name
    startTime
    endTime
    totalWorkingHours
    shiftType
    isOvernight
    gracePeriodMinutes
  }
}
        `;
    try {
      setPageState((prev) => ({ ...prev, loading: true }));

      const response = await axios.post(
        BASEURL,
        {
          query: query,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const shiftData = response.data?.data?.allShifts;
      if (shiftData) {
        setShiftList(shiftData);
        setPageState((prev) => ({ ...prev, loading: false, state: "success" }));
      } else {
        console.error("Error: Shift data not found in response.");
        setPageState((prev) => ({
          ...prev,
          loading: false,
          error: true,
          message: "No shift data available.",
        }));
      }
      setCirleLoading(false);
    } catch (error) {
      setCirleLoading(false);
      console.error("ResponseError:", error);
      setPageState((prev) => ({
        ...prev,
        loading: false,
        error: true,
        message: "Network error, please try again later.",
      }));
    }
  };

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="column" spacing={0}>
                <MaterialTypography variant="h5" fontWeight="medium">
                  Shift List
                </MaterialTypography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {AccessData &&
                  Array.isArray(AccessData) &&
                  AccessData.some(
                    (item) => item.component === "AddShift" && item.can_access
                  ) && (
                    <>
                      <MaterialButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => navigate("/shift/shift-roster")}
                      >
                        Shift Roster
                      </MaterialButton>
                      <MaterialButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleNewShift}
                      >
                        Add Shift
                      </MaterialButton>
                    </>
                  )}
              </Stack>
            </Stack>
          }
        />
        <MaterialDataGrid
          rows={shiftListTable.rows}
          columns={shiftListTable.columns}
          checkboxSelection={false}
          isSelectable={false}
          onRowClick={onShiftListClick}
          loading={pageState.loading}
          localeText={{
            noRowsLabel: (
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <MaterialTypography
                  variant="body2"
                  fontWeight="medium"
                  color="text"
                >
                  No shifts found
                </MaterialTypography>
                <MaterialTypography variant="caption" color="text">
                  You have not yet added any shift details. Click on the "Add
                  Shift" button to start adding shift details.
                </MaterialTypography>
              </Stack>
            ),
          }}
        />
      </Card>
    </DashboardLayout>
  );
};

export default Shift;
